import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import { getMonthRange } from '@/client/lib/dates';
import { trpc } from '@/client/utils/trpc';
import type { RouterOutputs } from '@/client/utils/trpc';
import { useEffect, useMemo, useState } from 'react';
import { MonthView } from './MonthView';
import { WeekNav } from './WeekNav';

type AdminWeekStatusRow = RouterOutputs['timesheets']['adminWeekStatus'][number];

interface AdminMonthPanelProps {
  employee: AdminWeekStatusRow | null;
  monthOffset: number;
  onOffsetChange: (n: number) => void;
}

export function AdminMonthPanel({ employee, monthOffset, onOffsetChange }: AdminMonthPanelProps) {
  const [projectFilter, setProjectFilter] = useState('');

  // biome-ignore lint/correctness/useExhaustiveDependencies: monthOffset is a prop, not a stable outer-scope value
  useEffect(() => {
    setProjectFilter('');
  }, [employee?.id, monthOffset]);

  const range = getMonthRange(monthOffset);
  const [yearStr, monthStr] = range.from.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);

  const { data: entries = [], isLoading } = trpc.timesheets.listEntries.useQuery(
    { date_from: range.from, date_to: range.to, user_id: employee?.id },
    { enabled: employee !== null },
  );
  const { data: submissions = [] } = trpc.timesheets.listSubmissions.useQuery(
    { user_id: employee?.id },
    { enabled: employee !== null },
  );

  const projectOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of entries) seen.set(e.project_id, e.project_name);
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [entries]);

  const filteredEntries = projectFilter
    ? entries.filter((e) => e.project_id === projectFilter)
    : entries;

  if (employee === null) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-sm text-muted-foreground">
          Select a team member to view their timesheet.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex items-center gap-4 border-b px-6 py-3">
        <div className="flex-1">
          <p className="text-sm font-semibold">{employee.full_name ?? '—'}</p>
          {employee.job_title && (
            <p className="text-xs text-muted-foreground">{employee.job_title}</p>
          )}
        </div>
        {!isLoading && projectOptions.length > 0 && (
          <Select
            value={projectFilter || '_all'}
            onValueChange={(v) => setProjectFilter(v === '_all' ? '' : v)}
          >
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All projects</SelectItem>
              {projectOptions.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <WeekNav
          label={range.label}
          onPrev={() => onOffsetChange(monthOffset - 1)}
          onNext={() => onOffsetChange(monthOffset + 1)}
          onToday={() => onOffsetChange(0)}
          showToday={monthOffset !== 0}
          todayLabel="This month"
        />
      </div>
      <div className="flex-1 overflow-y-auto p-6">
        <MonthView
          key={`${year}-${month}`}
          year={year}
          month={month}
          entries={filteredEntries}
          submissions={submissions}
          readOnly
        />
      </div>
    </div>
  );
}
